/**
 * ArtistPage Component
 */

import * as playerActions from '../../../redux/actions/player';
import { ArtistFragment } from '../../../relay/artist';
import { TaxonomyType } from '../../../relay/selectors/taxonomy';
import { getResponsiveAssets } from '../../../utils/image';
import './styles.scss';

import { RouteMatch } from 'found';
import { chain } from 'lodash';
import throttle from 'raf-throttle';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';

import Page from '../../../containers/Page';
import withLicenseBar from '../../../containers/withLicenseBar';
import { IQueryStringContext, QueryStringContext } from '../../../contexts/queryString';
import { toInnerHTML } from '../../../utils/string';
import LinkButton from '../../atoms/buttons/LinkButton';
import {
  IconApple,
  IconBandCamp,
  IconFacebook,
  IconInstagram,
  IconSoundCloud,
  IconSpotify,
  IconTwitter,
} from '../../atoms/images/Icon';
import Picture from '../../atoms/images/Picture';
import Thumbnail from '../../atoms/images/Thumbnail';
import Heading from '../../atoms/text/Heading';
import { Span } from '../../atoms/text/Label';
import Tag from '../../molecules/Tag';
import Collections from '../../organisms/content/Collections';
import { TrackListQuery } from '../../organisms/search/TrackList';
import ArtistHelmet, { ArtistTrackForSEO } from './helmet';

// Page Path
export const ArtistPagePath = (slug: string) => `/artist/${slug}`;

// Page Props
interface IOwnStateProps {
  currentTrackPlaying: string;
}

interface IOwnDispatchProps {
  pausePlayer(): void;
  playTrackVersion(versionId: string): void;
  setPlayerQueue(tracks: IPlayerTrack[]): void;
}

interface IOwnProps extends RouteMatch {
  artist: GQL.IArtist;
}

type IQueryStringContextProps = Pick<IQueryStringContext, 'addSearchFilter'>;

type HeaderLayoutType = 'horizontal' | 'vertical';

export type ArtistPageProps =
  & IOwnProps
  & IOwnStateProps
  & IOwnDispatchProps
  & IQueryStringContextProps;

interface IOwnState {
  featuredCollections: GQL.ICollection[];
  headerIsSticky?: boolean;
  terms: Array<{ name: string; count: number; }>;
  tracksForSEO: ArtistTrackForSEO[];
}

const classes = new BEM('artist');
const defaultList = [];

class ArtistPage extends React.Component<ArtistPageProps, IOwnState> {
  private artistImage: IResponsiveImage;
  private coverImage: IResponsiveImage;
  private headerLayout: HeaderLayoutType;
  private artistHasSocialPresence: boolean;

  private onScrollThrottled;
  private artistHeroImage: HTMLElement;
  private header: HTMLElement;
  // @ts-ignore
  private scrollArea: HTMLElement;

  constructor(props: ArtistPageProps) {
    super(props);
    const { artist } = props;

    this.artistImage = getResponsiveAssets('cover', artist.assets || defaultList);
    this.coverImage = getResponsiveAssets('thumb', artist.assets || defaultList);

    this.artistHasSocialPresence =
      !!artist.facebook
      || !!artist.twitter
      || !!artist.spotify
      || !!artist.itunes
      || !!artist.instagram
      || !!artist.soundcloud
      || !!artist.bandcamp;

    const imageRatio = this.artistImage.xlarge[0].metadata.width / this.artistImage.xlarge[0].metadata.height;
    this.headerLayout = imageRatio <= 1.05 ? 'vertical' : 'horizontal';

    this.onScrollThrottled = throttle(this.onScroll);

    this.state = {
      featuredCollections: defaultList,
      terms: defaultList,
      tracksForSEO: [],
    };
  }

  componentDidMount() {
    const el = findDOMNode(this) as HTMLElement;
    this.artistHeroImage = el.getElementsByClassName('artist__artist-picture')[0] as HTMLElement;
    this.header = el.getElementsByClassName('artist__header')[0] as HTMLElement;
    this.scrollArea = el.getElementsByClassName('page__main')[0] as HTMLElement;

    // tslint:disable-next-line:no-string-literal
    // if (!window['safari']) {
    window.addEventListener('scroll', this.onScrollThrottled);
    // }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScrollThrottled);
    this.onScrollThrottled = undefined;
  }

  onLoadArtistImage = (img: HTMLImageElement) => {
    const { width, height } = img.getBoundingClientRect();
    const ratio = width / height;
    const headerLayout = ratio <= 1.05 ? 'vertical' : 'horizontal';
    if (headerLayout !== this.headerLayout) {
      this.headerLayout = headerLayout;
      this.forceUpdate();
    }
  }

  onScroll = () => {
    if (this.headerLayout === 'vertical') {
      this.header.style.transform = `translate3d(0, -${window.scrollY * .5}px, 0)`;
    }
    this.artistHeroImage.style.transform = `translate3d(0, -${window.scrollY * .3}px, 0)`;
  }

  onTrackListResult = (results: GQL.ITrackSearchResult[]) => {
    const termsRanking = chain(results)
      // Gather all terms from track results
      .flatMap(({ terms }) => terms ? terms.edges!.map(({ node }) => node!) : defaultList)
      // Filter terms from Genre or Mood taxonomy
      .filter((term) => {
        const acceptedTaxonomies: TaxonomyType[] = [TaxonomyType.Genres, TaxonomyType.Moods];
        const termTaxonomy = term.taxonomy!.name as TaxonomyType;
        return acceptedTaxonomies.indexOf(termTaxonomy) > -1;
      })
      // Extract term names only
      .map(({ name }) => name)
      // Count all terms (returning a dictionary of the term name and the number of times it matched)
      .countBy()
      // Organize into an object for easier manipulation
      .map((count, name) => ({ name, count }))
      // Order the results by count descending
      .orderBy('count', 'desc')
      // Keep the top most used terms
      .slice(0, 15)
      .value();

    const featuredCollections = chain(results)
      .flatMap(({ collections }) => collections ? collections.edges!.map(({ node }) => node!) : defaultList)
      .uniqBy('id')
      .value();

    this.setState({
      featuredCollections,
      terms: termsRanking,
      tracksForSEO: results.map(({ title }) => ({ title })),
    });
  }

  renderHeader() {
    const { artist } = this.props;
    const titleSize = this.headerLayout === 'horizontal' ? 'h4' : 'h2';
    return (
      <div {...classes('header') }>
        <div {...classes('header__content-wrapper') }>
          <div {...classes('details') }>
            {this.headerLayout === 'horizontal' && (
              <Thumbnail
                responsiveAssets={this.coverImage}
                {...classes('cover-image') }
                size="xsmall"
              />
            )}
            <div {...classes('info') }>
              <Heading is={titleSize} {...classes('info__title') }>{artist.name!}</Heading>
              {this.headerLayout === 'horizontal' ? (
                <Span
                  text={artist.homebase || ''}
                  size="xsmall"
                  {...classes('info__subtitle') }
                />
              ) : (
                  <Heading is="h6" {...classes('info__subtitle') }>{artist.homebase || ''}</Heading>
                )}
            </div>
          </div>
          <div {...classes('header__actions') }>
            {/*<Button label="Share" icon={<IconShare />} {...classes('action') } />*/}
          </div>
        </div>
      </div>
    );
  }

  renderPicture() {
    return (
      <div {...classes('artist-picture') }>
        <Picture onLoad={this.onLoadArtistImage} assets={this.artistImage} {...classes('artist-picture__inner') } />
      </div>
    );
  }

  render() {
    const { artist } = this.props;

    return (
      <Page id="artist" layout="loose">
        <ArtistHelmet artist={artist} tracks={this.state.tracksForSEO} />
        <div {...classes('layout-wrapper') } data-header-layout={this.headerLayout}>
          <section className="page__main">
            <div {...classes('header__margin') } />
            {this.renderPicture()}
            {this.renderHeader()}
            <div {...classes('content') }>
              <div {...classes('content__wrapper') }>
                <div {...classes('content__column', 'bio') }>
                  <Heading is="h3">About</Heading>
                  {!!artist.bio && (
                    <p {...classes('about') } dangerouslySetInnerHTML={toInnerHTML(artist.bio)} />
                  )}
                  {!!this.state.terms.length && (<>
                    <div {...classes('tags-group') }>
                      {this.state.terms.map(({ count, name }) => (
                        <Tag
                          key={name}
                          id={name}
                          label={name}
                          cardinality={count}
                          onClick={() => this.props.addSearchFilter({name: name!})}
                        />
                      ))}
                    </div>
                  </>)}
                </div>
                <div {...classes('content__column', 'tracks') }>
                  <TrackListQuery
                    actionSource="artist_page"
                    cardProps={{
                      backgroundColor: 'bw2',
                      size: 'small',
                    }}
                    onResult={this.onTrackListResult}
                    searchQuery={{
                      count: 1000,
                      filters: {
                        artist_slug: artist.slug!,
                      },
                    }}
                    title="Tracks"
                    {...classes('track-listing') }
                  />
                </div>
                {this.artistHasSocialPresence && (
                  <div {...classes('content__column', 'social') }>
                    <div {...classes('social-links') }>
                      <Heading is="h3">Connect</Heading>
                      {!!artist.facebook &&
                        <LinkButton external to={artist.facebook} label="Facebook" icon={<IconFacebook />} />
                      }
                      {!!artist.twitter &&
                        <LinkButton external to={artist.twitter} label="Twitter" icon={<IconTwitter />} />
                      }
                      {!!artist.spotify &&
                        <LinkButton external to={artist.spotify} label="Spotify" icon={<IconSpotify />} />
                      }
                      {!!artist.itunes &&
                        <LinkButton external to={artist.itunes} label="Apple Music" icon={<IconApple />} />
                      }
                      {!!artist.instagram &&
                        <LinkButton external to={artist.instagram} label="Instagram" icon={<IconInstagram />} />
                      }
                      {!!artist.soundcloud &&
                        <LinkButton external to={artist.soundcloud} label="SoundCloud" icon={<IconSoundCloud />} />
                      }
                      {!!artist.bandcamp &&
                        <LinkButton external to={artist.bandcamp} label="Bandcamp" icon={<IconBandCamp />} />
                      }
                    </div>
                  </div>
                )}
              </div>
              {!!this.state.featuredCollections.length &&
                <div {...classes('content__wrapper') }>
                  <div {...classes('content__column', 'collections') }>
                    <Heading is="h3">Featured in Collections</Heading>
                    <Collections data={this.state.featuredCollections} gridView hideTitle />
                  </div>
                </div>
              }
            </div>
          </section>
        </div>
      </Page>
    );
  }
}

const ArtistPageWithLicenseBar = withLicenseBar(ArtistPage);

// tslint:disable-next-line:max-line-length
const ConnectedArtistPage = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps & IQueryStringContextProps, IStoreState>(
  (state) => {
    const {
      currentTrack,
      isTrackPlaying,
    } = state.player;

    let currentTrackPlaying = '';

    if (isTrackPlaying && currentTrack) {
      currentTrackPlaying = currentTrack.trackId;
    }

    return {
      currentTrackPlaying,
    };
  },
  (dispatch) => ({
    pausePlayer: () => {
      dispatch(playerActions.pause());
    },
    playTrackVersion: (trackId) => {
      dispatch(playerActions.playTrackFromQueue({ trackId }));
    },
    setPlayerQueue: (tracks: IPlayerTrack[]) => {
      dispatch(playerActions.addTracksToQueue({ tracks }));
    },
  }),
)(ArtistPageWithLicenseBar);

const RelayArtistPage = createFragmentContainer(
  ConnectedArtistPage,
  ArtistFragment,
);

const ArtistPageWithQueryStringContext: React.SFC<IOwnProps> = (props) => (
  <QueryStringContext.Consumer>
    {({ addSearchFilter }) => <RelayArtistPage addSearchFilter={addSearchFilter} {...props} />}
  </QueryStringContext.Consumer>
);

export default ArtistPageWithQueryStringContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Artist/index.tsx